import React from 'react';
import { useNavigate } from 'react-router-dom';
import Reveal from './Reveal';
import { useMunicipalities } from '../../hooks/useMunicipalities';

const municipalityGridClassName = 'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8';

const Municipalities: React.FC = () => {
    const navigate = useNavigate();
    const { data: municipalities = [], isLoading } = useMunicipalities();

    if (isLoading) return <div className="bg-brand-darkGreen py-16 text-center text-white">Carregando Municípios...</div>;

    return (
        <section className="relative overflow-hidden bg-brand-darkGreen py-14 lg:py-16" id="municipios">
            <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

            <div className="container mx-auto px-4 relative z-10">
                <Reveal>
                    <div className="mb-10 grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-end">
                        <div>
                            <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-brand-yellow text-xs font-bold uppercase tracking-widest">
                                <i className="fas fa-users"></i>
                                <span>Rede integrada</span>
                            </div>
                            <h2 className="max-w-3xl text-3xl font-extrabold leading-tight text-white md:text-4xl">
                                Municípios conectados em uma rede regional de proteção
                            </h2>
                            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">
                                Acesse a página de cada cidade para acompanhar conselheiros, dados institucionais e contatos atualizados.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3 rounded-lg border border-white/15 bg-white/10 p-3 backdrop-blur-sm">
                            <div className="rounded-lg bg-white px-4 py-3 text-brand-darkGreen">
                                <strong className="block text-2xl font-black">{municipalities.length}</strong>
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Municípios</span>
                            </div>
                            <a
                                href="#contato"
                                className="flex items-center justify-center gap-2 rounded-lg bg-brand-yellow px-4 py-3 text-center text-xs font-black uppercase tracking-wider text-brand-darkGreen transition hover:bg-yellow-300"
                            >
                                <i className="fab fa-whatsapp"></i>
                                Atualizar dados
                            </a>
                        </div>
                    </div>
                </Reveal>

                <div className={municipalityGridClassName}>
                    {municipalities.map((city, idx) => (
                        <Reveal key={city.slug} delay={idx * 50}>
                            <button
                                onClick={() => navigate(`/municipio/${city.slug}`)}
                                className="group flex h-full w-full flex-col items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] p-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-yellow/50 hover:bg-white/10"
                            >
                                <div className="relative">
                                    <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-brand-yellow/30 bg-white shadow-md transition-all duration-300 group-hover:border-brand-yellow md:h-[72px] md:w-[72px]">
                                        <img
                                            src={city.image || 'https://via.placeholder.com/150'}
                                            alt={`Brasão de ${city.name}`}
                                            className="h-full w-full object-cover p-1"
                                        />
                                    </div>
                                    <div className="absolute -right-2 -top-2 flex h-7 w-7 translate-y-1 items-center justify-center rounded-lg bg-brand-yellow text-xs text-brand-darkGreen opacity-0 shadow-md transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                                        <i className="fas fa-arrow-right"></i>
                                    </div>
                                </div>

                                <h3 className="min-h-[2.25rem] text-center text-xs font-bold leading-tight text-white transition-colors group-hover:text-brand-yellow md:text-sm">
                                    {city.name}
                                </h3>
                            </button>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={400}>
                    <div className="mt-8 text-center">
                        <p className="text-xs text-slate-400">
                            Dados municipais são mantidos pela administração regional e pelos acessos restritos dos municípios.
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default Municipalities;
